import {
    Alert,
    AlertColor,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Divider,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { CloseOutlined, DeleteOutlined, SaveOutlined } from '@mui/icons-material';
import { InfoMessage, InfoMessageProps } from '../../../components/InfoMessage';
import { AmcInstance, ProvisionDetails } from '../../../types/AmcInstance';
import { AmcOwningEntity } from '../../../types/AmcOwningEntity';
import ApiService from '../../../ApiService';
import ConfirmDialog, { ConfirmDialogProps } from '../../../components/ConfirmDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dealer } from '../../../types/Dealer';
import FilteredDealerSelector from './FilteredDealerSelector';
import DealerSelector from '../../../components/DealerSelector';

type AmcInstanceFormProps = {
    save?: boolean;
    size?: 'lg' | 'md' | 'sm';
    hideActions?: boolean;
    amcInstance: AmcInstance;
    dealer?: Dealer;
    // onSubmit: () => void;
    onValidate?: (validated: boolean) => void;
    onSave: (amcInstance: AmcInstance) => void;
};

export default function AmcInstanceForm(props: AmcInstanceFormProps) {
    const { onValidate } = props;

    const navigate = useNavigate();
    const location = useLocation();
    const [amcInstance, setAmcInstance] = useState<AmcInstance>(props.amcInstance ?? location.state);
    const [amcOwningEntities, setAmcOwningEntities] = useState<AmcOwningEntity[]>([]);
    const [busy, setBusy] = useState(false);
    // const [invalidDealer, setInvalidDealer] = useState(false);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });
    const [isDirty, setDirty] = useState(false);
    const [confirmDialogProps, setConfirmDialogProps] = useState<ConfirmDialogProps | null>(null);
    const [dealer, setDealer] = useState<Dealer | null>(props.dealer ?? null);
    const [dealerError, setDealerError] = useState(false);

    const validated: boolean | undefined = useMemo(() => {
        if (amcInstance) {
            if (!amcInstance?.owningEntityId) {
                return false;
            }

            if (!amcInstance?.name) {
                return false;
            }

            if (!amcInstance?.instanceName) {
                return false;
            }

            if (!isDirty) {
                return false;
            }

            // if (invalidDealer) {
            //     return false;
            // }

            return true;
        }

        return false;
    }, [amcInstance, isDirty]);

    useEffect(() => {
        ApiService.getAmcOwningEntities().then((response) => {
            setAmcOwningEntities(response.data);
        });
    }, []);

    useEffect(() => {
        if (props.save === true) {
            submit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.save]);

    useEffect(() => {
        if (onValidate) {
            onValidate(validated);
        }
    }, [onValidate, validated]);

    const showActions: boolean | undefined = useMemo(() => {
        return props.hideActions ? false : true;
    }, [props.hideActions]);

    // const isEditing: boolean | undefined = useMemo(() => {
    //     return amcInstance.id > 0;
    // }, [amcInstance]);

    function showInfoMessage(alertColor: AlertColor, message: string) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
            },
        });
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const field = event.target.name;
        let value = event.target.value;

        switch (field) {
            case 'instanceName':
            case 'instanceId':
                value = value.replace(/\s/g, ''); // Remove spaces
                break;
        }

        setAmcInstance({ ...amcInstance, [field]: value });
        setDirty(true);
        if (field === 'name') {
        }
    }

    function provisionInstance() {
        const provisionDetails = new ProvisionDetails();
        provisionDetails.advertiserName = amcInstance.name;
        provisionDetails.instanceId = amcInstance.instanceId;
        provisionDetails.instanceName = amcInstance.instanceName;
        provisionDetails.owningEntityId = amcInstance.owningEntityId;
        provisionDetails.dealerCountryCode = dealer?.countryCode;

        ApiService.provisionAmcInstance(provisionDetails)
            .then((response) => {
                showInfoMessage('success', 'AMC Instance has been provisioned.');
                setAmcInstance({ ...response.data, isNew: false });
                setDirty(false);

                props.onSave({ ...response.data, isNew: false });
            })
            .catch((error) => {
                showInfoMessage('error', error.message);
            });
    }

    function submit() {
        if (!amcInstance.id) {
            provisionInstance();
        } else {
            amcInstance.status = 'UPDATING';
            amcInstance.updatedAdvertiser = dealer?.dspAdvertiser || '';
            ApiService.updateAmcInstance(amcInstance)
                .then((response) => {
                    showInfoMessage('success', 'AMC Instance has been updated.');
                    setAmcInstance({ ...response.data, isNew: false });
                    setDirty(false);

                    props.onSave({ ...response.data, isNew: false });
                })
                .catch((error) => {
                    showInfoMessage('error', error.message);
                });
        }
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        submit();
    }

    function showDeleteConfirm() {
        setConfirmDialogProps({
            title: 'Delete AMC Instance ' + amcInstance.name + ' ?',
            text: '',
            onOk: () => deleteAmcInstance(),
            okText: 'Delete',
            okColor: 'error',
            okIcon: <DeleteOutlined />,
            onCancel: () => hideConfirm(),
        });
    }

    function hideConfirm() {
        setConfirmDialogProps(null);
    }

    function deleteAmcInstance() {
        hideConfirm();
        setBusy(true);
        if (amcInstance.id != null) {
            ApiService.deleteAmcInstance(amcInstance.id)
                .then(() => {
                    setBusy(false);
                    showInfoMessage('success', 'AMC Instance has been deleted.');
                    setTimeout(() => {
                        navigate('/amcInstances');
                    }, 2000);
                })
                .catch((error) => {
                    showInfoMessage('error', error.message);
                });
        }
    }

    let uploadMessage;
    if (busy) {
        uploadMessage = (
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open
            >
                <Box sx={{ display: 'flex' }}>
                    <CircularProgress />
                </Box>
            </Backdrop>
        );
    }

    function replaceSpcialCharAndSpace(text: string | undefined) {
        if (!text) return '';
        // Allowlist to match what Amazon actually accepts in an AMC instance name:
        // only [0-9 a-z A-Z]. The instance name is built without spaces, so strip
        // everything else. A blocklist here previously missed en/em dashes and accented
        // letters, which then failed server-side at create time.
        return text.replace(/[^a-zA-Z0-9]/g, '');
    }

    function selectDealer(dealderId: number, dealer: Dealer) {
        if (dealer && (dealer.amcInstanceId || !dealer.dspAdvertiser)) {
            setDealerError(true);
            setDirty(false);
            return;
        } else {
            setDealerError(false);
        }
        setDealer(dealer);

        if (props?.dealer && props?.dealer?.id !== dealer.id) {
            setDirty(true);
        }

        if (dealderId && dealer) {
            if (!amcInstance.id) {
                const countryCode: string = dealer.countryCode ? dealer.countryCode : '';
                const agencyId: string = dealer.agency ? dealer.agency?.id.toString() : '';
                const subLength: number = countryCode.length + agencyId.length;
                const dealerName: string = replaceSpcialCharAndSpace(dealer.dealerName);
                const length: number =
                    dealerName.length + subLength <= 50 ? 50 : dealerName.length - (dealerName.length - 50 + subLength);
                const instanceName = `${agencyId}${dealerName.substring(0, length)}${countryCode}`;
                setAmcInstance({ ...amcInstance, name: dealer?.dealerName, instanceName: instanceName });
            }
        } else {
            setAmcInstance({ ...amcInstance, name: '', instanceName: '' });
        }
    }

    return (
        <>
            <form method="post" onSubmit={handleSubmit}>
                {uploadMessage}

                {confirmDialogProps && (
                    <>
                        <ConfirmDialog
                            title={confirmDialogProps.title}
                            text={confirmDialogProps.text}
                            okText={confirmDialogProps.okText}
                            okIcon={confirmDialogProps.okIcon}
                            okColor={confirmDialogProps.okColor}
                            onCancel={confirmDialogProps.onCancel}
                            onOk={confirmDialogProps.onOk}
                        />
                    </>
                )}
                <Grid container spacing={3}>
                    <Grid size={props.size === 'md' ? 5 : 7}>
                        <TextField
                            fullWidth
                            name="owningEntityId"
                            label="AMC Owning Entity"
                            select
                            required={true}
                            size="small"
                            value={amcInstance.owningEntityId}
                            onChange={handleChange}
                            slotProps={{
                                select: { native: false },
                                inputLabel: { shrink: true },
                            }}
                        >
                            {amcOwningEntities.map((amcOwningEntity) => {
                                return (
                                    <MenuItem key={amcOwningEntity.amcEntityId} value={amcOwningEntity.amcEntityId}>
                                        {amcOwningEntity.name}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    </Grid>

                    <Grid size={props.size === 'md' ? 7 : 5} />
                    {!amcInstance.id && (
                        <>
                            <Grid size={props.size === 'md' ? 5 : 7}>
                                <FilteredDealerSelector
                                    dealerId={dealer?.id}
                                    variant="outlined"
                                    dealerSelected={selectDealer}
                                    TextFieldProps={{
                                        fullWidth: true,
                                    }}
                                />
                                <Alert sx={{ marginTop: '10px' }} severity="info">
                                    Advertisers will not appear in this list if they are already assigned to an AMC
                                    Instance or if they do not have a DSP Advertiser ID assigned.
                                </Alert>
                            </Grid>
                        </>
                    )}

                    {amcInstance.id > 0 && (
                        <>
                            <Grid size={props.size === 'md' ? 5 : 7}>
                                <DealerSelector
                                    dealerId={dealer?.id}
                                    variant="outlined"
                                    dealerSelected={selectDealer}
                                    showInactiveAdvertisersToggle={true}
                                />
                                {dealerError && (
                                    <Alert sx={{ marginTop: '10px' }} severity="error">
                                        Advertiser already assigned to an AMC Instance or it does not have a DSP
                                        Advertiser ID assigned.
                                    </Alert>
                                )}
                            </Grid>
                            <Grid size={props.size === 'md' ? 7 : 5} />
                            <Grid size={props.size === 'md' ? 5 : 7}>
                                <TextField
                                    name="instanceName"
                                    label="Instance Name"
                                    value={amcInstance.instanceName}
                                    disabled={true}
                                    required={true}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>
                            <Grid size={props.size === 'md' ? 7 : 5} />

                            <Grid size={props.size === 'md' ? 5 : 7}>
                                <TextField
                                    name="instanceId"
                                    label="Instance ID"
                                    value={amcInstance.instanceId}
                                    disabled={true}
                                    required={true}
                                    fullWidth={true}
                                    size="small"
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>

                            <Grid size={props.size === 'md' ? 7 : 5} />

                            <Grid size={props.size === 'md' ? 5 : 7}>
                                <TextField
                                    name="awsAccountId"
                                    label="AWS Account ID"
                                    value={amcInstance.awsAccountId}
                                    required={false}
                                    fullWidth={true}
                                    size="small"
                                    disabled={true}
                                    variant="outlined"
                                    onChange={handleChange}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                    }}
                                />
                            </Grid>
                            <Grid size={props.size === 'md' ? 7 : 5} />
                        </>
                    )}

                    <Grid size={12}></Grid>
                </Grid>

                {showActions && (
                    <>
                        <Divider />

                        <Grid container spacing={2}>
                            <Grid
                                style={{ marginTop: '30px' }}
                                size={{
                                    md: 12,
                                }}
                            ></Grid>
                            <Grid size={12}>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        disabled={!isDirty}
                                        color="primary"
                                        type="submit"
                                        variant="contained"
                                        startIcon={<SaveOutlined />}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/amcInstances')}
                                        color="error"
                                        startIcon={<CloseOutlined />}
                                    >
                                        Cancel
                                    </Button>
                                    <div style={{ flex: 1 }} />
                                    {!amcInstance.isNew && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteOutlined />}
                                            onClick={() => showDeleteConfirm()}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </>
                )}
            </form>
            <Box sx={{ minHeight: '100%', p: 1 }}></Box>
            <InfoMessage {...infoMessage} />
        </>
    );
}
